import { apiClient } from './apiClient'

export interface ContentTypeRequest {
  embedId: string
}

export interface ContentFilters {
  minPrice?: number
  maxPrice?: number
  minBedrooms?: number
  maxBedrooms?: number
  minBathrooms?: number
  maxBathrooms?: number
  propertyType?: string
  status?: string
  searchLocation?: string
  searchRadius?: number
  page?: number
  pageSize?: number
  sort?: string
  order?: string
  fields?: string
  dir?: string
  broker?: string
}

export interface ContentTypeResponse {
  // Define la estructura de respuesta según tu API
  success: boolean
  data?: any
  message?: string
  // Agrega más campos según la respuesta real de tu API
}

export interface ContentTypeByEmbedRequest {
  embedId: string
}

export interface ContentTypeByEmbedResponse {
  success: boolean
  embedId: string
  vendorId: number
  locationId: number
  data: {
    code: string
    name: string
    config: any
    vendorId: number
    locationId: number
  }
}

/**
 * Servicio para obtener contenido por embedId
 * Hace la petición a la API: GET /ContentType/listContentTypeByEmbed?embedId=xxx
 * (El baseURL ya incluye /api/hq-nd)
 */
export const getContentByEmbedId = async (
  embedId: string, 
  encriptedString?: string, 
  filters?: ContentFilters
): Promise<ContentTypeResponse> => {
  console.log('=== getContentByEmbedId called ===')
  console.log('embedId', embedId)
  console.log('encriptedString', encriptedString)
  console.log('filters', filters)
  
  try {
    const params: any = { embedId }
    
    // Si hay encriptedString, agregarlo a los parámetros
    if (encriptedString) {
      params.encriptedString = encriptedString
    }
    
    // Agregar filtros si existen
    if (filters && Object.keys(filters).length > 0) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params[key] = value
        }
      })
    }
    
    console.log('Making API request with params:', params)
    
    const response = await apiClient.get('/api/hq-nd/ContentType/listContentTypeByEmbed', {
      params
    })
    
    console.log('API response received:', response.data)
    
    return {
      success: true,
      data: response.data,
      message: 'Contenido obtenido exitosamente'
    }
  } catch (error: any) {
    console.error('Error al obtener contenido por embedId:', error)
    
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Error al obtener contenido'
    }
  }
}

/**
 * Servicio para obtener el tipo de contenido por embedId
 * Hace la petición a la API: GET /ContentType/getContentTypeByEmbed?embedId=xxx
 */
export const getContentTypeByEmbed = async (embedId: string): Promise<ContentTypeByEmbedResponse> => {
  try {
    const response = await apiClient.get('/api/hq-nd/ContentType/getContentTypeByEmbed', {
      params: {
        embedId
      }
    })
    
    return response.data
  } catch (error: any) {
    console.error('Error al obtener tipo de contenido por embedId:', error)
    throw error
  }
}

/**
 * Servicio específico para FeaturedPropertiesCarousel
 */
export const getFeaturedPropertiesCarousel = async (): Promise<ContentTypeResponse> => {
  return getContentByEmbedId('FeaturedPropertiesCarousel')
}

/**
 * Servicio para obtener un agente aleatorio
 * Hace la petición a la API: GET /ContentType/listContentTypeByEmbed?embedId=xxx&random_agent=Y
 */
export const getRandomAgent = async (
  embedId: string = "ap_5b0e9c7ad4312f86b2c4e9a103d7f5c8_qn5h0z2y",
  encriptedString?: string,
  agentId?: string | number
): Promise<ContentTypeResponse> => {
  console.log('=== getRandomAgent called ===')
  console.log('embedId', embedId)
  console.log('encriptedString', encriptedString)
  console.log('agentId', agentId)
  
  try {
    const params: any = { 
      embedId,
      random_agent: "Y"
    }
    
    // Si hay encriptedString, agregarlo a los parámetros
    if (encriptedString) {
      params.encriptedString = encriptedString
    }
    
    // Si hay agentId, agregarlo a los parámetros
    if (agentId !== undefined && agentId !== null) {
      params.id = agentId
    }
    
    console.log('Making API request with params:', params)
    
    const response = await apiClient.get('/api/hq-nd/ContentType/listContentTypeByEmbed', {
      params
    })
    
    console.log('API response received:', response.data)
    
    return {
      success: true,
      data: response.data,
      message: 'Agente aleatorio obtenido exitosamente'
    }
  } catch (error: any) {
    console.error('Error al obtener agente aleatorio:', error)
    
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Error al obtener agente aleatorio'
    }
  }
}

/**
 * Servicio para obtener propiedades similares por ID
 * Hace la petición a la API: POST /ContentType/listSimilarPropertiesById
 */
export interface SimilarPropertiesRequest {
  id: number
  embedId?: string
  sameZip?: boolean
  limit?: number
}

export interface SimilarPropertiesResponse {
  success: boolean
  page: number
  pageSize: number
  total: number
  data: Array<{
    id: string
    address: string
    city: string
    state: string
    postalCode: string
    latitude: number
    longitude: number
    listPrice: number
    bedrooms: number
    bathrooms: number
    area?: number
    sqft?: number
    photos?: string[]
    photo?: string
    coverImage?: string
    imageUrl?: string
    [key: string]: any
  }>
}

export const getSimilarPropertiesById = async (
  request: SimilarPropertiesRequest
): Promise<SimilarPropertiesResponse> => {
  console.log('=== getSimilarPropertiesById called ===')
  console.log('request', request)
  
  try {
    const response = await apiClient.get(
      '/api/hq-nd/ContentType/listSimilarPropertiesById',
      {
        params: {
          id: request.id,
          sameZip: request.sameZip || false,
          limit: request.limit || 5
        }
      }
    )
    
    console.log('API response received:', response.data)
    
    return response.data
  } catch (error: any) {
    console.error('Error al obtener propiedades similares:', error)
    
    throw new Error(
      error.response?.data?.message || 
      error.message || 
      'Error al obtener propiedades similares'
    )
  }
}

export default {
  getContentByEmbedId,
  getContentTypeByEmbed,
  getFeaturedPropertiesCarousel,
  getRandomAgent,
  getSimilarPropertiesById
}
